import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import { listClassConfigurations, createClassConfiguration, deleteClassConfiguration } from "../../api/classConfigurationApi";
import { createLookupApi } from "../../api/lookupApi";
import "../Users/UsersPage.css";

const classApi = createLookupApi("classes");
const shiftApi = createLookupApi("shifts");
const sectionApi = createLookupApi("sections");

const emptyForm = { classId: "", shiftId: "", sectionId: "", roomNo: "", serial: 0 };

/**
 * Manages the class × shift × section combinations students actually enroll
 * into (see backend ClassConfiguration javadoc) — a prerequisite for the
 * Student registration form's class dropdown.
 */
export default function ClassConfigurationPage() {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([listClassConfigurations(), classApi.list(), shiftApi.list(), sectionApi.list()])
      .then(([configs, classList, shiftList, sectionList]) => {
        setItems(configs);
        setClasses(classList);
        setShifts(shiftList);
        setSections(sectionList);
      })
      .catch(() => setError("Failed to load class configurations."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await createClassConfiguration({
        classId: Number(form.classId),
        shiftId: Number(form.shiftId),
        sectionId: Number(form.sectionId),
        roomNo: form.roomNo,
        serial: Number(form.serial),
      });
      setSuccess("Class configuration created.");
      setModalOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.classConfigName}"?`)) {
      return;
    }
    setError("");
    try {
      await deleteClassConfiguration(item.id);
      setSuccess("Class configuration deleted.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "classConfigName", label: "Class Configuration" },
    { key: "roomNo", label: "Room No" },
    { key: "serial", label: "Serial" },
    { key: "status", label: "Status", render: (row) => (row.status === 1 ? "Active" : "Inactive") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Class Configuration</h1>
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
          + Add Class Configuration
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          actions={(item) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(item)}>
              Delete
            </button>
          )}
        />
      )}

      {modalOpen && (
        <Modal title="Add Class Configuration" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit} className="user-form">
            <label>Class</label>
            <select value={form.classId} onChange={(e) => setForm({ ...form, classId: e.target.value })} required>
              <option value="" disabled>
                Select a class
              </option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <label>Shift</label>
            <select value={form.shiftId} onChange={(e) => setForm({ ...form, shiftId: e.target.value })} required>
              <option value="" disabled>
                Select a shift
              </option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <label>Section</label>
            <select value={form.sectionId} onChange={(e) => setForm({ ...form, sectionId: e.target.value })} required>
              <option value="" disabled>
                Select a section
              </option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <label>Room No</label>
            <input value={form.roomNo} onChange={(e) => setForm({ ...form, roomNo: e.target.value })} />

            <label>Serial</label>
            <input type="number" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} />

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
